import { Box, Card, CardContent, Grid, Typography, Skeleton, Stack, Avatar, LinearProgress, Chip } from '@mui/material';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import { useQuery } from '@tanstack/react-query';
import { tenantService } from '../../services/tenant.service';
import { StatusChip } from '../../components/common/StatusChip';
import { DataTable } from '../../components/common/DataTable';
import { StatCard } from '../../components/common/StatCard';
import type { Tenant } from '../../types';
import { format } from 'date-fns';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as ReTooltip } from 'recharts';
import { formatINRCompact } from '../../lib/currency';

// ── Donut chart label ─────────────────────────────────────────────────────────
const PIE_DATA_CONFIG = [
  { key: 'activeTenants',    label: 'Active',    color: '#10B981' },
  { key: 'inactiveTenants',  label: 'Inactive',  color: '#94A3B8' },
  { key: 'suspendedTenants', label: 'Suspended', color: '#EF4444' },
];

// ── Main dashboard ────────────────────────────────────────────────────────────
export function SuperAdminDashboard() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['super-admin-stats'],
    queryFn: () => tenantService.getStats(),
  });

  const { data: tenantsData, isLoading: tenantsLoading } = useQuery({
    queryKey: ['tenants-recent'],
    queryFn: () => tenantService.getAll({ page: 1, limit: 8 }),
  });

    const statsAny = stats as unknown as Record<string, number> | undefined;
  const pieData = PIE_DATA_CONFIG.map((c) => ({
    name: c.label,
    value: statsAny ? statsAny[c.key] ?? 0 : 0,
    color: c.color,
  }));

  const totalTenants = stats?.totalTenants ?? 0;
  const activeTenants = stats?.activeTenants ?? 0;
  const activePct = totalTenants ? Math.round((activeTenants / totalTenants) * 100) : 0;

  const columns = [
    {
      id: 'name',
      label: 'Company',
      minWidth: 200,
      render: (row: Tenant) => (
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: '#E8EEF8',
              color: '#1D4ED8',
              fontSize: '0.8rem',
              fontWeight: 700,
              borderRadius: '10px',
            }}
          >
            {row.name.slice(0, 2).toUpperCase()}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
              {row.name}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontFamily: 'monospace' }}>
              {row.slug}
            </Typography>
          </Box>
        </Stack>
      ),
    },
    {
      id: 'plan',
      label: 'Plan',
      render: (row: Tenant) => (
        <Chip
          label={row.plan?.name ?? '—'}
          size="small"
          sx={{ fontWeight: 600, bgcolor: '#E8EEF8', color: '#1E4BB8', borderRadius: '6px', border: 'none' }}
        />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      render: (row: Tenant) => <StatusChip status={row.status} />,
    },
    {
      id: 'employees',
      label: 'Employees',
      render: (row: Tenant) => (
        <Stack spacing={0.5} sx={{ minWidth: 80 }}>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary' }}>
            {row.employeeCount ?? 0}
            <Typography component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
              {' '}/ {row.employeeLimit === -1 ? '∞' : row.employeeLimit}
            </Typography>
          </Typography>
          {row.employeeLimit > 0 && (row.employeeCount ?? 0) > 0 && (
            <LinearProgress
              variant="determinate"
              value={Math.min(100, ((row.employeeCount ?? 0) / row.employeeLimit) * 100)}
              sx={{ height: 4, borderRadius: 4, bgcolor: '#EEF1F6' }}
            />
          )}
        </Stack>
      ),
    },
    {
      id: 'createdAt',
      label: 'Joined',
      render: (row: Tenant) => (
        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
          {format(new Date(row.createdAt), 'MMM d, yyyy')}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      {/* ── KPI cards ────────────────────────────────────── */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard
            title="Total Tenants"
            value={stats?.totalTenants ?? 0}
            change="Across all plans"
            icon={<BusinessRoundedIcon />}
            tone="primary"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard
            title="Active Tenants"
            value={stats?.activeTenants ?? 0}
            change={`${activePct}% of total tenants`}
            icon={<CheckCircleRoundedIcon />}
            tone="success"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard
            title="Total Employees"
            value={stats?.totalEmployees ?? 0}
            change="Across all tenants"
            icon={<PeopleRoundedIcon />}
            tone="info"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <StatCard
            title="Monthly Revenue"
            value={formatINRCompact(stats?.platformRevenue ?? 0)}
            change="Estimated MRR"
            icon={<AttachMoneyRoundedIcon />}
            tone="warning"
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* ── Charts + table row ───────────────────────────── */}
      <Grid container spacing={2.5}>
        {/* Tenant status donut */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                Tenant Health
              </Typography>
              <Typography variant="caption" color="text.secondary" mb={2} display="block">
                Distribution by status
              </Typography>

              {isLoading ? (
                <Skeleton variant="circular" width={180} height={180} sx={{ mx: 'auto', my: 2 }} />
              ) : (
                <Box sx={{ position: 'relative' }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={58}
                        outerRadius={85}
                        paddingAngle={3}
                        dataKey="value"
                        strokeWidth={0}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Pie>
                      <ReTooltip
                        contentStyle={{
                          borderRadius: 10,
                          border: '1px solid #E2E8F0',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                          fontSize: 13,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Centre label */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: 'text.primary', lineHeight: 1 }}>
                      {totalTenants}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                      total
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Legend */}
              <Stack spacing={1.25} mt={2}>
                {PIE_DATA_CONFIG.map((c) => {
                  const val = statsAny ? statsAny[c.key] ?? 0 : 0;
                  const pct = totalTenants ? Math.round((val / totalTenants) * 100) : 0;
                  return (
                    <Stack key={c.key} direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: c.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontWeight: 500 }}>
                          {c.label}
                        </Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: 'text.primary' }}>{val}</Typography>
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>({pct}%)</Typography>
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>

              {/* Active rate bar */}
              <Box mt={2.5}>
                <Stack direction="row" justifyContent="space-between" mb={0.75}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.secondary' }}>Active rate</Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <TrendingUpRoundedIcon sx={{ fontSize: 14, color: 'success.main' }} />
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'success.main' }}>{activePct}%</Typography>
                  </Stack>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={activePct}
                  color="success"
                  sx={{ height: 8, borderRadius: 8 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent tenants table */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ pb: '24px !important' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="subtitle1" fontWeight={700}>Recent Tenants</Typography>
                <Chip
                  label={`${totalTenants} total`}
                  size="small"
                  sx={{ bgcolor: '#E8EEF8', color: '#1E4BB8', fontWeight: 600 }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Latest company accounts on the platform
              </Typography>
              <DataTable
                columns={columns}
                rows={tenantsData?.data ?? []}
                getRowId={(r) => r.id}
                isLoading={tenantsLoading}
                emptyMessage="No tenants yet"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
